import { useMemo } from 'react'
import {
  LineChart as MantineLineChart,
  BarChart as MantineBarChart,
} from '@mantine/charts'
import { useMediaQuery } from '@mantine/hooks'
import dayjs from 'dayjs'
import { CompareSubject } from './Compare'
import { useFullYearData } from '../widgets/hooks/useFullYearData'
import { useWidgetContext } from '../widgets/providers'
import { fmt } from '../widgets/format'
import { useMetricFormatting } from '../widgets/api'
import { FULL_MONTH } from '../stories/months'

type CompareChartProps = {
  subject: CompareSubject
  comparand: CompareSubject
  view: string
  component: string
}

export default function CompareChart({
  subject,
  comparand,
  view,
  component,
}: CompareChartProps) {
  const isDesktop = useMediaQuery('(min-width: 800px)')
  const ctx = useWidgetContext()
  const competitionYear = ctx.competition_start
    ? dayjs(ctx.competition_start).year()
    : dayjs().year()
  const { data: formatting } = useMetricFormatting()

  // Fetch data for subject
  const subjectData = useFullYearData(
    competitionYear,
    subject?.type || 'player',
    subject?.id || 0,
    [component, component],
    ['value', 'value_ytd']
  )

  // Fetch data for comparand
  const comparandData = useFullYearData(
    competitionYear,
    comparand?.type || 'player',
    comparand?.id || 0,
    [component, component],
    ['value', 'value_ytd']
  )

  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ]

  // Transform data for dual-line chart
  const chartData = useMemo(() => {
    return months.map((month, idx) => {
      const subjectValue =
        component === 'score'
          ? subjectData[idx]?.data[component]?.value_ytd
          : subjectData[idx]?.data[component]?.value
      const comparandValue =
        component === 'score'
          ? comparandData[idx]?.data[component]?.value_ytd
          : comparandData[idx]?.data[component]?.value

      return {
        label: month,
        [subject?.name || 'Subject']: subjectValue,
        [comparand?.name || 'Comparand']: comparandValue,
      }
    })
  }, [
    subjectData,
    comparandData,
    months,
    component,
    subject?.name,
    comparand?.name,
  ])

  // Define series for both lines
  const series = useMemo(
    () => [
      { name: subject?.name || 'Subject', color: '#8073AD' },
      { name: comparand?.name || 'Comparand', color: '#B2675E' },
    ],
    [subject?.name, comparand?.name]
  )

  // Create formatter using pure fmt function (not a hook)
  const formatter = useMemo(() => {
    return (value: number | null) => {
      return fmt(ctx.metric || '', component, 'value', value, formatting)
    }
  }, [ctx.metric, component, formatting])

  // Only render if both subjects are selected
  if (!subject || !comparand) {
    return null
  }

  const tooltipStyles = {
    tooltip: {
      backgroundColor: 'light-dark(white, var(--mantine-color-gray-9))',
    },
  }

  // Render based on view type
  if (view === 'line') {
    return (
      <MantineLineChart
        h={400}
        w="100%"
        data={chartData}
        dataKey="label"
        series={series}
        curveType="linear"
        connectNulls={false}
        dotProps={{ r: 4 }}
        valueFormatter={formatter}
        legendProps={{ verticalAlign: 'bottom', height: 50 }}
        styles={tooltipStyles}
        xAxisProps={{
          tickFormatter: (label: string) => (isDesktop ? label : label[0]),
        }}
        tooltipProps={{
          labelFormatter: (label: string) => FULL_MONTH[label] || label,
        }}
      />
    )
  }

  if (view === 'bar') {
    return (
      <MantineBarChart
        h={400}
        w="100%"
        data={chartData}
        dataKey="label"
        series={series}
        valueFormatter={formatter}
        legendProps={{ verticalAlign: 'bottom', height: 50 }}
        styles={tooltipStyles}
        xAxisProps={{
          tickFormatter: (label: string) => (isDesktop ? label : label[0]),
        }}
        tooltipProps={{
          labelFormatter: (label: string) => FULL_MONTH[label] || label,
        }}
      />
    )
  }

  // Return null for other views (like 'table')
  return null
}
